import * as THREE from "three";
import { bus } from "../core/bus";

// Anti-air threats: static AA guns (damage over time while you loiter low or
// tight) and MANPADS teams (single high-damage missile, defeated by flares).
// This is what makes altitude and orbit choice a decision instead of a
// slider — docs/design/GDD.md §Survivability.
//
// Pure decision helpers are exported for tests; the manager owns meshes,
// timers, and bus traffic. Aircraft hull/HP lives in the engine.

export type FlakKind = "aa" | "manpads";

export type FlakSiteDef = {
  // Hidden sites (SEAD [HIDDEN] variant) render nothing until they fire —
  // you learn where they are the hard way. Still hittable by area fire.
  hidden?: boolean;
  kind: FlakKind;
  // [x, elevation-above-terrain, z] — y lifts the site onto rooftops
  // (IRON MARKET's rooftop guns); 0 = ground level.
  origin: [number, number, number];
};

// Altitude bands (world units). The flight envelope is 120..350.
export const ALT_LOW_MAX = 200;
export const ALT_HIGH_MIN = 280;

export type AltBand = "LOW" | "MED" | "HIGH";

export function altitudeBand(altitude: number): AltBand {
  if (altitude < ALT_LOW_MAX) return "LOW";
  if (altitude >= ALT_HIGH_MIN) return "HIGH";
  return "MED";
}

// Extra aim dispersion (radians) from altitude — low and slow shoots tight.
export function altitudeDispersion(altitude: number): number {
  const band = altitudeBand(altitude);
  return band === "LOW" ? 0.0006 : band === "MED" ? 0.0035 : 0.008;
}

export const AA_RANGE = 430;
export const MANPADS_RANGE = 520;
// Orbiting the enemy anchor pins you over their positions — sites near the
// orbit center light up regardless of altitude.
export const ORBIT_EXPOSURE_RADIUS = 280;

// A site wakes when the aircraft is in range AND exposed: flying LOW, or
// parked on the enemy-cluster orbit with the site under the orbit.
export function isSiteActive(
  siteToAircraft: number,
  altitude: number,
  siteToOrbitCenter: number,
  orbitingEnemy: boolean,
  range: number,
): boolean {
  if (siteToAircraft > range) return false;
  if (altitudeBand(altitude) === "LOW") return true;
  return orbitingEnemy && siteToOrbitCenter < ORBIT_EXPOSURE_RADIUS;
}

// Per-burst hit chance for AA guns by altitude band.
export function aaHitChance(altitude: number): number {
  const band = altitudeBand(altitude);
  return band === "LOW" ? 0.5 : band === "MED" ? 0.28 : 0.12;
}

const AA_BURST_INTERVAL = 2.8;
const AA_BURST_DAMAGE = 7;
const MANPADS_COOLDOWN = 22;
const MISSILE_FLIGHT_TIME = 4.2;
const MISSILE_HIT_CHANCE = 0.85;
const MISSILE_DAMAGE = 34;

type Site = {
  def: FlakSiteDef;
  position: THREE.Vector3;
  mesh: THREE.Group;
  alive: boolean;
  timer: number; // burst timer (aa) / cooldown (manpads)
};

export type Missile = {
  from: THREE.Vector3;
  t: number; // 0..MISSILE_FLIGHT_TIME
  defeated: boolean;
};

function buildAAGunMesh(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x3d4038, roughness: 0.9, flatShading: true });
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.8, 1.2, 8, 1, true), new THREE.MeshStandardMaterial({ color: 0x7a6f52, roughness: 1, flatShading: true, side: THREE.DoubleSide }));
  ring.position.y = 0.6;
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.5, 1.4, 6), mat);
  base.position.y = 0.9;
  const barrels = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 4.4), mat);
  barrels.position.set(0, 1.9, 1.2);
  barrels.rotation.x = -Math.PI / 3.2; // pointed up
  g.add(ring, base, barrels);
  return g;
}

function buildManpadsMesh(): THREE.Group {
  const g = new THREE.Group();
  const crate = new THREE.Mesh(
    new THREE.BoxGeometry(1.8, 0.9, 1.1),
    new THREE.MeshStandardMaterial({ color: 0x4a5240, roughness: 1, flatShading: true }),
  );
  crate.position.y = 0.45;
  const tube = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, 0.16, 2.0, 6),
    new THREE.MeshStandardMaterial({ color: 0x2e332b, roughness: 0.8, flatShading: true }),
  );
  tube.position.set(0.4, 1.15, 0);
  tube.rotation.z = -Math.PI / 3;
  g.add(crate, tube);
  return g;
}

export class FlakManager {
  readonly group = new THREE.Group();
  private readonly sites: Site[] = [];
  private missile: Missile | null = null; // one inbound at a time is plenty of pressure
  private readonly _tmp = new THREE.Vector3();

  constructor(defs: FlakSiteDef[], terrainHeightAt: (x: number, z: number) => number) {
    this.group.name = "flak-sites";
    for (const def of defs) {
      const mesh = def.kind === "aa" ? buildAAGunMesh() : buildManpadsMesh();
      const y = terrainHeightAt(def.origin[0], def.origin[2]) + def.origin[1];
      mesh.position.set(def.origin[0], y, def.origin[2]);
      mesh.traverse((o) => {
        o.userData["isFlakSite"] = true;
        o.castShadow = true;
      });
      // Small props: never block LOS or navigation
      mesh.userData["losTransparent"] = true;
      if (def.hidden) mesh.visible = false;
      this.group.add(mesh);
      this.sites.push({
        def,
        position: mesh.position.clone(),
        mesh,
        alive: true,
        timer: def.kind === "aa" ? AA_BURST_INTERVAL : MANPADS_COOLDOWN * 0.4,
      });
    }
  }

  get aliveCount(): number {
    return this.sites.filter((s) => s.alive).length;
  }

  get totalCount(): number {
    return this.sites.length;
  }

  get missileInbound(): boolean {
    return this.missile !== null && !this.missile.defeated;
  }

  // Player pops flares: defeats any inbound missile. Returns true if one died.
  deployFlares(): boolean {
    if (this.missile && !this.missile.defeated) {
      this.missile.defeated = true;
      return true;
    }
    return false;
  }

  update(
    dt: number,
    aircraftPos: THREE.Vector3,
    altitude: number,
    orbitCenter: { x: number; z: number },
    orbitingEnemy: boolean,
  ): void {
    for (const site of this.sites) {
      if (!site.alive) continue;
      const toAircraft = this._tmp.copy(aircraftPos).sub(site.position).length();
      const toOrbit = Math.hypot(site.position.x - orbitCenter.x, site.position.z - orbitCenter.z);
      const range = site.def.kind === "aa" ? AA_RANGE : MANPADS_RANGE;
      const active = isSiteActive(toAircraft, altitude, toOrbit, orbitingEnemy, range);

      site.timer -= dt;
      if (!active) {
        // Guns spool back up when you leave; MANPADS keep their cooldown
        if (site.def.kind === "aa") site.timer = Math.max(site.timer, AA_BURST_INTERVAL * 0.5);
        continue;
      }

      if (site.timer > 0) continue;

      if (site.def.kind === "aa") {
        site.timer = AA_BURST_INTERVAL;
        if (site.def.hidden && !site.mesh.visible && site.alive) site.mesh.visible = true;
        bus.emit("flak:burst", { from: site.position.clone(), to: aircraftPos.clone() });
        if (Math.random() < aaHitChance(altitude)) {
          bus.emit("aircraft:hit", { amount: AA_BURST_DAMAGE, source: "aa" });
        }
      } else if (!this.missile) {
        site.timer = MANPADS_COOLDOWN;
        if (site.def.hidden && !site.mesh.visible && site.alive) site.mesh.visible = true;
        this.missile = { from: site.position.clone(), t: 0, defeated: false };
        bus.emit("flak:missileLaunch", { from: site.position.clone() });
      }
    }

    if (this.missile) {
      this.missile.t += dt;
      if (this.missile.t >= MISSILE_FLIGHT_TIME) {
        if (!this.missile.defeated && Math.random() < MISSILE_HIT_CHANCE) {
          bus.emit("aircraft:hit", { amount: MISSILE_DAMAGE, source: "manpads" });
        }
        bus.emit("flak:missileEnded", { defeated: this.missile.defeated });
        this.missile = null;
      }
    }
  }

  // Player ordnance vs sites. Returns number destroyed.
  hitInRadius(center: THREE.Vector3, radius: number): number {
    let destroyed = 0;
    for (const site of this.sites) {
      if (!site.alive) continue;
      if (site.position.distanceTo(center) > radius + 3.5) continue;
      this.destroySite(site);
      destroyed++;
    }
    return destroyed;
  }

  // Direct hitscan hit on a site mesh.
  hitObject(obj: THREE.Object3D): boolean {
    let node: THREE.Object3D | null = obj;
    while (node) {
      const site = this.sites.find((s) => s.mesh === node && s.alive);
      if (site) {
        this.destroySite(site);
        return true;
      }
      node = node.parent;
    }
    return false;
  }

  private destroySite(site: Site): void {
    site.alive = false;
    site.mesh.visible = false;
    bus.emit("flak:destroyed", { kind: site.def.kind, position: site.position.clone() });
  }

  dispose(): void {
    this.group.removeFromParent();
  }
}
